import { v4 as uuidv4 } from 'uuid'
import store from '../store'
import { qs } from '../utils'
import { getCookie } from '../utils/cookie'
import { getDeviceId } from '../utils/device'

/**
 * Selectors for all focusable elements
 * @type {string}
 */
const FOCUSABLE_ELEMENT_SELECTORS =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]'
const KEY_CODE_MAP = {
  TAB: 9,
}

function loopFocus(config) {
  if (!config) {
    throw new Error('Could not initialize focus-trapping - Config Missing')
  }
  const el = config.el,
    focusElement = config.focusElement

  if (!el) {
    throw new Error('Could not initialize focus-trapping - Element Missing')
  }

  const focusableElements = el.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS)
  let keyboardHandler

  //There can be containers without any focusable element
  if (focusableElements.length > 0) {
    const firstFocusableEl = focusableElements[0],
      lastFocusableEl = focusableElements[focusableElements.length - 1],
      elementToFocus = focusElement ? focusElement : firstFocusableEl
    elementToFocus.focus()

    keyboardHandler = function keyboardHandler(e) {
      if (e.keyCode === KEY_CODE_MAP.TAB) {
        //Rotate Focus
        if (e.shiftKey && document.activeElement === firstFocusableEl) {
          e.preventDefault()
          lastFocusableEl.focus()
        } else if (!e.shiftKey && document.activeElement === lastFocusableEl) {
          e.preventDefault()
          firstFocusableEl.focus()
        }
      }
    }
    el.addEventListener('keydown', keyboardHandler)
  }

  return function cleanUp() {
    if (keyboardHandler) {
      el.removeEventListener('keydown', keyboardHandler)
    }
  }
}

class Modal {
  DOM
  activeElement
  constructor() {
    this.DOM = {
      el: qs('#modal'),
      close: qs('.modal-close'),
      overlay: qs('.modal-overlay'),
      form: qs('#modal form'),
      inputSMS: qs('#modal input'),
      succesMessage: qs('#modal .get-app-success'),
      errorMessage: qs('#modal .get-app-error'),
    }

    this.binds()
  }

  binds() {
    this.DOM.close.addEventListener('click', () => {
      this.close()
    })
    this.DOM.overlay.addEventListener('click', () => {
      this.close()
    })

    this.DOM.form.addEventListener('submit', (e) => {
      e.preventDefault()
      grecaptcha.execute()
    })

    this.DOM.form.addEventListener('smsRecaptchaComplete', (e) => {
      const token = e.detail
      grecaptcha.reset()
      this.sendSMS(token)
    })
  }

  open() {
    this.activeElement = document.activeElement
    this.DOM.el.classList.toggle('is-inview')
    this.DOM.el.style = 'display: block'
    store.dom.body.classList.add('no-scroll')
    this.DOM.el?.setAttribute?.('aria-modal', 'true')
    this.DOM.el?.setAttribute?.('aria-hidden', 'false')
    document.querySelector?.('#header')?.setAttribute('aria-hidden', 'true')
    document.querySelector?.('[data-router-wrapper]')?.setAttribute('aria-hidden', 'true')
    document.querySelector?.('footer')?.setAttribute('aria-hidden', 'true')
    document.querySelector?.('.disclaimer')?.setAttribute?.('aria-hidden', 'true')
    document.querySelector?.('.download-fix')?.setAttribute?.('aria-hidden', 'true')

    loopFocus({
      el: this.DOM.el,
      focusElement: this.DOM.inputSMS,
    })
  }

  close() {
    this.DOM.el.classList.remove('is-inview')
    this.DOM.el.style = 'display: none'
    this.DOM.el?.setAttribute?.('aria-modal', 'false')
    this.DOM.el?.setAttribute?.('aria-hidden', 'true')
    document.querySelector('#header')?.setAttribute?.('aria-hidden', 'false')
    document.querySelector('[data-router-wrapper]')?.setAttribute?.('aria-hidden', 'false')
    document.querySelector('footer')?.setAttribute?.('aria-hidden', 'false')
    document.querySelector('.disclaimer')?.setAttribute?.('aria-hidden', 'false')
    document.querySelector('.download-fix')?.setAttribute?.('aria-hidden', 'false')
    store.dom.body?.classList?.remove?.('no-scroll')
    if (this.activeElement) this.activeElement.focus()
  }

  sendSMS(token) {
    const phone = this.DOM.inputSMS.value
    const paid = qs('body').dataset.paid

    var utmCampaign

    switch (paid) {
      case 'SEARCH_BRAND':
        utmCampaign = 'Adwords_UA_Web_Brand_CPC'
        break
      case 'SEARCH_CMP':
        utmCampaign = 'Adwords_UA_Web_NBCMP_CPC'
        break
      case 'SEARCH_NB':
        utmCampaign = 'Adwords_UA_Web_NBFUNC_CPC'
        break
      default:
        break
    }
    const deviceId = getDeviceId()
    const anonymousUserId = getCookie('ajs_anonymous_id') || uuidv4()
    fetch(`${process.env.API_BASE_URL}/download/sendLink`, {
      method: 'POST',
      body: JSON.stringify({
        phoneNumber: phone,
        utmParams: {
          t: 'g0tzh4z_hid5o3',
          utm_campaign: utmCampaign,
        },
      }),
      headers: {
        accept: 'text/plain',
        'content-type': 'application/json',
        'X-Recaptcha-Token': token,
        'Device-Id': deviceId,
        'Static-Device-Id': deviceId,
        os: 'browser',
        'X-Request-Id': uuidv4(),
        'Earnin-Client-Version': '2.0.0',
        'X-Anonymous-User-Id': anonymousUserId,
      },
    })
      .then((res) => {
        if (res.status >= 400 && res.status < 600) {
          throw new Error('Unable to send sms')
        }
        this.DOM.succesMessage.classList.add('show')
        this.DOM.form.reset()
        setTimeout(() => {
          this.DOM.succesMessage.classList.remove('show')
        }, 8000)

        if (paid) {
          const callback = function () {}
          gtag('event', 'conversion', {
            send_to: 'AW-973217545/h4AWCNLN9u8BEIm-iNAD',
            event_callback: callback,
          })
        }
      })
      .catch((res) => {
        this.DOM.errorMessage.classList.add('show')
        this.DOM.form.reset()
        setTimeout(() => {
          this.DOM.errorMessage.classList.remove('show')
        }, 8000)
      })
  }
}

export default Modal
